import { useState } from "react";
import { VStack } from "@chakra-ui/react";
import { Field } from "@hydrologiq/shared/chakra/Field";
import {
  NativeSelectRoot,
  NativeSelectField,
} from "@hydrologiq/shared/chakra/NativeSelect";
import Popover from "@hydrologiq/shared/chakra/Popover";
import Form from "@hydrologiq/shared/chakra/custom/Form";
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert";
import {
  showSuccessToast,
  showErrorToast,
  showLoadingToast,
  dismissToast,
} from "@hydrologiq/shared/utils/toast";

import {
  CreateAlertDocument,
  AlertTypeEnum,
  AlertSeverityEnum,
  AlertSourceEnum,
  CreateAlertMutation,
  CreateAlertMutationVariables,
} from "@hydrologiq/gql-experience/graphql";
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient";

interface AlertCreateProps {
  onAlertCreated?: () => void;
}

export function AlertCreate({ onAlertCreated }: AlertCreateProps) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copy = {
    buttonCreate: "Create",
    headingAlert: "Alert",
    messageCreated: "created",
    messageSuccessfullyCreated: "successfully created",
    messageCreationFailed: "Creation failed",
    messageValidationError: "Validation error",
    messageAddingToSystem: "Adding to system...",
    formPleaseProvide: "Please provide",
    formInformationBelow: "information below",
    formEnter: "Enter",
    labelType: "Type",
    labelSeverity: "Severity",
    labelSource: "Source",
    labelAsset: "Asset",
    labelSystem: "System",
    labelMessage: "Message",
    actionCreating: "Creating",
    commonAn: "an",
    helperAssetOrSystem:
      "Provide an asset ID, a system ID, or both (system tags the alert).",
  };

  const [createAlertMutation, { loading }] = useMutationWithEndpoint<
    CreateAlertMutation,
    CreateAlertMutationVariables
  >(
    CreateAlertDocument,
    {
      refetchQueries: ["GetAlerts", "GetAlertHeadlines"],
      awaitRefetchQueries: true,
    },
    "primary"
  );

  const handleSubmit = async (data: FormData) => {
    let loadingToastId: string | undefined;
    try {
      setError(null);

      loadingToastId = showLoadingToast(
        `${copy.actionCreating} ${copy.headingAlert}`,
        copy.messageAddingToSystem
      );

      const type = data.get("type")?.toString();
      const severity = data.get("severity")?.toString();
      const source = data.get("source")?.toString();
      const asset = data.get("asset")?.toString()?.trim() ?? "";
      const system = data.get("system")?.toString()?.trim() ?? "";
      const message = data.get("message")?.toString();

      if (!asset && !system) {
        const errorMessage = `${copy.formPleaseProvide} ${
          copy.commonAn
        } asset ID or system ID (or both).`;
        setError(errorMessage);
        showErrorToast(copy.messageValidationError, errorMessage);
        return false;
      }

      if (!message || !message.trim()) {
        const errorMessage = `${copy.formPleaseProvide} ${
          copy.commonAn
        } ${copy.headingAlert.toLowerCase()} ${copy.labelMessage.toLowerCase()}`;
        setError(errorMessage);
        showErrorToast(copy.messageValidationError, errorMessage);
        return false;
      }

      if (!type || !severity || !source) {
        const errorMessage = `${copy.formPleaseProvide} ${
          copy.commonAn
        } ${copy.headingAlert.toLowerCase()} ${copy.labelType.toLowerCase()}`;
        setError(errorMessage);
        showErrorToast(copy.messageValidationError, errorMessage);
        return false;
      }

      const input: CreateAlertMutationVariables["input"] = {
        type: type as AlertTypeEnum,
        severity: severity as AlertSeverityEnum,
        source: source as AlertSourceEnum,
        message: message.trim(),
        metadata: {},
        additionalAssets: [],
        additionalSystems: [],
        ...(asset ? { asset } : {}),
        ...(system ? { system } : {}),
      };

      const result = await createAlertMutation({
        variables: { input },
      });

      if (result.data?.createAlert) {
        showSuccessToast(
          `${copy.headingAlert} ${copy.messageCreated}`,
          `${copy.headingAlert} ${copy.messageSuccessfullyCreated}.`
        );

        setOpen(false);
        setError(null);
        onAlertCreated?.();
        return true;
      } else {
        const errorMessage = `Failed to create ${copy.headingAlert.toLowerCase()}. Please try again.`;
        setError(errorMessage);
        showErrorToast(copy.messageCreationFailed, errorMessage);
        return false;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `An unexpected error occurred while creating the ${copy.headingAlert.toLowerCase()}`;
      setError(errorMessage);
      showErrorToast(copy.messageCreationFailed, errorMessage);
      return false;
    } finally {
      if (loadingToastId) {
        dismissToast(loadingToastId);
      }
    }
  };

  const handleCancel = () => {
    setError(null);
    setOpen(false);
  };

  return (
    <Popover
      actionType="create"
      actionVariant="primary"
      placement="right-end"
      loading={loading}
      onOpenChange={setOpen}
      open={open}
    >
      <Form
        heading={`${copy.buttonCreate} ${copy.commonAn} ${copy.headingAlert}`}
        helperText={`${
          copy.formPleaseProvide
        } ${copy.headingAlert.toLowerCase()} ${copy.formInformationBelow}. ${copy.helperAssetOrSystem}`}
        actionType="create"
        loading={loading}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      >
        <VStack gap={5}>
          {error && (
            <ErrorAlert
              title={`${copy.headingAlert} Creation Error`}
              message={error}
              onClose={() => setError(null)}
            />
          )}

          <Field label={copy.labelType} required>
            <NativeSelectRoot>
              <NativeSelectField
                name="type"
                items={[
                  { label: "Status", value: AlertTypeEnum.Status },
                  { label: "PAD", value: AlertTypeEnum.Pad },
                  { label: "Maintenance", value: AlertTypeEnum.Maintenance },
                  { label: "Anomaly", value: AlertTypeEnum.Anomaly },
                  { label: "Performance", value: AlertTypeEnum.Performance },
                ]}
              />
            </NativeSelectRoot>
          </Field>

          <Field label={copy.labelSeverity} required>
            <NativeSelectRoot>
              <NativeSelectField
                name="severity"
                items={[
                  { label: "Critical", value: AlertSeverityEnum.Critical },
                  { label: "Warning", value: AlertSeverityEnum.Warning },
                  { label: "Info", value: AlertSeverityEnum.Info },
                ]}
              />
            </NativeSelectRoot>
          </Field>

          <Field label={copy.labelSource} required>
            <NativeSelectRoot>
              <NativeSelectField
                name="source"
                items={[
                  { label: "Internal", value: AlertSourceEnum.Internal },
                  { label: "Grafana", value: AlertSourceEnum.Grafana },
                  { label: "Vendor", value: AlertSourceEnum.Vendor },
                ]}
              />
            </NativeSelectRoot>
          </Field>

          <Field label={copy.labelAsset}>
            <input
              name="asset"
              placeholder={`${copy.formEnter} asset UUID (optional if system is set)`}
            />
          </Field>

          <Field label={copy.labelSystem}>
            <input
              name="system"
              placeholder={`${copy.formEnter} system UUID (optional if asset is set)`}
            />
          </Field>

          <Field label={copy.labelMessage} required>
            <textarea
              name="message"
              placeholder={`${
                copy.formEnter
              } ${copy.headingAlert.toLowerCase()} ${copy.labelMessage.toLowerCase()}`}
              rows={3}
            />
          </Field>
        </VStack>
      </Form>
    </Popover>
  );
}

export default AlertCreate;
